import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Search, X } from 'lucide-react';
import SingleSelect from '../Select/Select';
import AuctioneerPreview from '../AuctioneerPreview/AuctioneerPreview';
import ProgressIndicator from '../ProgressIndicator/ProgressIndicator';
import Pagination from '../Pagination/Pagination';
import TextInput from '../TextInput';
import { getAuctioneerListUrl } from '../../utils/urls';
import { AUCTIONEERS_PER_PAGE } from '../../utils/gateways';
import { getFilterParamsFromMatch } from '../../utils/auctioneer';
import { KEYCODE_ENTER, STATES } from '../../constants';

import './AuctioneerList.css';

const AuctioneerList = ({
    loadData,
    onInitialize,
    prefetchPage,
    auctioneers,
    onAssociationChange,
    hideAssociationFilter,
    selectedAssociation,
    auctioneersLoading,
    match,
    onSearchChange,
}) => {
    var filterParams = getFilterParamsFromMatch(match);
    var page = filterParams.page;
    var showingResultsFor = filterParams.search;
    const [search, setSearch] = useState(showingResultsFor || '');

    // Get params safely without optional chaining
    var params = match && match.params ? match.params : {};

    // Initialize and load data on mount
    useEffect(() => {
        if (onInitialize) onInitialize();
        if (loadData) loadData();
    }, []); // Run once on mount

    // Handle URL changes
    useEffect(() => {
        var urlFilterParams = getFilterParamsFromMatch(match);
        var urlSearch = urlFilterParams.search;
        var cleanSearch = urlSearch ? decodeURIComponent(urlSearch) : '';
        if (cleanSearch !== '-') {
            setSearch(cleanSearch);
        }
        if (loadData) loadData();
    }, [params.page, params.search, params.association]);

    var pageNumber = useMemo(function() {
        return page ? parseInt(page, 10) : 1;
    }, [page]);

    // Prefetch adjacent pages when current page loads
    useEffect(function() {
        if (!auctioneersLoading && auctioneers && auctioneers.length > 0 && prefetchPage) {
            var current = pageNumber;

            // Prefetch previous page (for back navigation)
            if (current > 1) {
                prefetchPage(current - 1, undefined, showingResultsFor, selectedAssociation);
            }

            // Prefetch next pages (for forward navigation)
            var hasMorePages = auctioneers.length >= AUCTIONEERS_PER_PAGE;
            if (hasMorePages) {
                prefetchPage(current + 1, undefined, showingResultsFor, selectedAssociation);
                // Also prefetch page+2 for aggressive caching
                prefetchPage(current + 2, undefined, showingResultsFor, selectedAssociation);
            }
        }
    }, [auctioneersLoading, auctioneers, pageNumber, prefetchPage, showingResultsFor, selectedAssociation]);

    // Handle hover prefetch
    var handlePrefetchPage = useCallback(function(pageNum) {
        if (prefetchPage) {
            prefetchPage(pageNum, undefined, showingResultsFor, selectedAssociation);
        }
    }, [prefetchPage, showingResultsFor, selectedAssociation]);

    var handleSearchKeyDown = function(e) {
        if (e.keyCode === KEYCODE_ENTER) {
            persistSearch();
        }
    };

    var handleSearchChange = function(e) {
        var value = (e.target.value || '').replace(/[^A-Za-z0-9\-'\s]+/g, '');
        setSearch(value);
    };

    var handleClear = function() {
        setSearch('');
        onSearchChange('', false);
    };

    var persistSearch = function(shouldReplace) {
        onSearchChange(search, shouldReplace || false);
    };

    // Render content
    var content;
    if (auctioneersLoading && (!auctioneers || !auctioneers.length)) {
        content = (
            <div className="al-auctioneers-loading">
                <ProgressIndicator text="Loading auctioneers..." />
            </div>
        );
    } else if (auctioneers && auctioneers.length) {
        var gridClass = 'al-auctioneers-grid' + (auctioneersLoading ? ' al-auctioneers-grid--loading' : '');
        content = (
            <div className={gridClass}>
                {auctioneers.map(function(auctioneer) {
                    return (
                        <AuctioneerPreview key={auctioneer.id} auctioneer={auctioneer} />
                    );
                })}
            </div>
        );
    } else {
        content = (
            <div className="al-auctioneers-empty">
                <p>No auctioneers found</p>
            </div>
        );
    }

    var renderPagination = function() {
        if (!auctioneers || !auctioneers.length) return null;
        return (
            <Pagination
                currentPage={page}
                getUrl={function(p) { return getAuctioneerListUrl(p, undefined, showingResultsFor); }}
                hideNexts={auctioneers.length < AUCTIONEERS_PER_PAGE}
                loading={auctioneersLoading}
                onPrefetch={handlePrefetchPage}
            />
        );
    };

    return (
        <div className="al-auctioneer-list">
            {/* Header */}
            <header className="al-auctioneer-list-header">
                <h1>Our Auctioneers</h1>
            </header>

            {/* Search & Filters */}
            <div className="al-auctioneer-filters">
                {/* Search */}
                <div className="al-filter-group al-filter-group--search">
                    <div className="al-search-input-wrapper">
                        <TextInput
                            value={search}
                            onChange={handleSearchChange}
                            onKeyDown={handleSearchKeyDown}
                            placeholder="Search by name, city, or postal code"
                            id="al_auctioneer-name-search"
                        />
                        <button
                            type="button"
                            className="al-button al-button--primary al-search-btn"
                            onClick={persistSearch}
                        >
                            <Search size={18} />
                        </button>
                    </div>
                    {showingResultsFor && (
                        <div className="al-search-chip">
                            <span>Searching for "{showingResultsFor}"</span>
                            <button
                                type="button"
                                className="al-search-chip-clear"
                                onClick={handleClear}
                            >
                                <X size={16} />
                            </button>
                        </div>
                    )}
                </div>

                {/* Association Filter */}
                {!hideAssociationFilter && (
                    <div className="al-filter-group">
                        <SingleSelect
                            options={STATES}
                            value={selectedAssociation}
                            onChange={onAssociationChange}
                            unselectedText="Filter by association"
                        />
                    </div>
                )}
            </div>

            {/* Pagination Top */}
            {renderPagination()}

            {/* Content */}
            {content}

            {/* Pagination Bottom */}
            {renderPagination()}
        </div>
    );
};

AuctioneerList.propTypes = {
    loadData: PropTypes.func.isRequired,
    onInitialize: PropTypes.func,
    prefetchPage: PropTypes.func,
    auctioneers: PropTypes.arrayOf(PropTypes.object),
    onAssociationChange: PropTypes.func,
    hideAssociationFilter: PropTypes.bool,
    selectedAssociation: PropTypes.string,
    auctioneersLoading: PropTypes.bool,
    match: PropTypes.object,
    onSearchChange: PropTypes.func,
};

export default AuctioneerList;
